import { motion } from 'framer-motion';
import { ArrowRight, Check, X, Zap, Sparkles, Target, Package, XCircle, Quote, MessageSquare } from 'lucide-react';
import proof1 from './assets/proof-1.jpg';
import proof2 from './assets/proof-2.jpg';
import proof3 from './assets/proof-3.jpg';

const STRIPE_URL = 'https://buy.stripe.com/28E28sb4lgzx18V1ph1Nu01';

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

/* ─── CTA ─── */

function CTAPrimary({ text = 'Start My 3 Day Build', className = '' }: { text?: string; className?: string }) {
  return (
    <a
      href={STRIPE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`cta-glow group inline-flex items-center justify-center gap-3 bg-brand-gold text-white font-bold rounded-full transition-all duration-300 hover:bg-brand-gold-deep hover:scale-[1.03] active:scale-[0.97] shadow-cta px-12 py-[1.15rem] text-lg tracking-wide ${className}`}
    >
      {text}
      <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1.5" />
    </a>
  );
}

function CTAGold({ text = 'Start My 3 Day Build' }: { text?: string }) {
  return (
    <a
      href={STRIPE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="cta-glow group inline-flex items-center justify-center gap-3 bg-brand-gold text-white font-bold rounded-full px-14 py-[1.15rem] text-lg tracking-wide transition-all duration-300 hover:bg-brand-gold-deep hover:scale-[1.03] active:scale-[0.97] shadow-cta"
    >
      {text}
      <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1.5" />
    </a>
  );
}

/* ─── 1. HERO ─── */

function HeroSection() {
  return (
    <section className="pt-24 pb-28 sm:pt-36 sm:pb-40 px-5 section-warm">
      <div className="max-w-narrow mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="inline-flex items-center gap-2 bg-brand-gold/10 text-brand-gold-dark border border-brand-gold/25 rounded-full px-5 py-2 text-sm font-semibold mb-10 tracking-wide">
            <Zap className="w-3.5 h-3.5" />
            3 Day Client System™
          </div>
        </motion.div>

        <motion.h1
          className="text-[2.75rem] sm:text-6xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight text-brand-black mb-10"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        >
          Turn Your Existing Traffic Into{' '}
          <span className="text-brand-gold-dark">Paying Clients in 3 Days</span>
        </motion.h1>

        <motion.p
          className="text-lg sm:text-xl text-brand-gray max-w-md mx-auto mb-6 leading-[1.7]"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          We build landing pages that convert for founders and service businesses who already have attention but are not turning it into revenue.
        </motion.p>

        <motion.p
          className="text-base text-brand-black font-semibold mb-4 tracking-wide"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          Built in 3 days · Focused on conversion · Nothing extra
        </motion.p>

        <motion.p
          className="text-sm text-brand-gold-dark font-semibold mb-14 tracking-wide"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          Limited weekly spots
        </motion.p>

        <motion.div
          className="inline-block bg-white/60 backdrop-blur-sm rounded-[2rem] px-8 py-5 shadow-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <CTAPrimary />
        </motion.div>
      </div>
    </section>
  );
}

/* ─── 2. PROBLEM ─── */

function ProblemSection() {
  const problems = [
    'Your offer is not clear in the first few seconds.',
    'Your page looks good but does not convert.',
    'You get attention but no consistent clients.',
  ];

  return (
    <section className="py-24 sm:py-32 px-5 section-dark">
      <div className="max-w-content mx-auto text-center">
        <motion.h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-14 tracking-tight" {...fadeUp}>
          Sound Familiar?
        </motion.h2>

        <div className="space-y-4 mb-14">
          {problems.map((text, i) => (
            <motion.div
              key={i}
              className="flex items-center gap-4 text-left bg-white/[0.04] border border-red-500/20 rounded-2xl px-7 py-6 backdrop-blur-sm"
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.12 }}
            >
              <span className="w-9 h-9 rounded-full bg-red-500/15 flex items-center justify-center flex-shrink-0">
                <X className="w-4 h-4 text-red-400" />
              </span>
              <span className="text-lg text-white/90 font-medium">{text}</span>
            </motion.div>
          ))}
        </div>

        <motion.p className="text-xl text-white/50 mb-3" {...fadeUp}>
          This is not a traffic problem.
        </motion.p>
        <motion.p className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight" {...fadeUp}>
          It is a structure problem.
        </motion.p>
      </div>
    </section>
  );
}

/* ─── 3. SOLUTION ─── */

function SolutionSection() {
  return (
    <section className="py-16 sm:py-20 px-5 section-warm">
      <div className="max-w-content mx-auto text-center">
        <motion.p className="text-xl sm:text-2xl font-semibold text-brand-black leading-[1.6]" {...fadeUp}>
          We take your existing offer and build a landing page that converts. Done in 3 days.
        </motion.p>
      </div>
    </section>
  );
}

/* ─── 4. SYSTEM ─── */

function SystemSection() {
  const features = [
    'Make your offer clear immediately',
    'Remove confusion before action',
    'Guide users from first click to decision',
  ];

  return (
    <section className="py-20 sm:py-28 px-5 section-cream">
      <div className="max-w-content mx-auto text-center">
        <motion.div {...fadeUp}>
          <div className="w-14 h-14 rounded-2xl bg-brand-gold/10 flex items-center justify-center mx-auto mb-7">
            <Sparkles className="w-7 h-7 text-brand-gold-dark" />
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-extrabold text-brand-black mb-4 tracking-tight">
            The 3 Day Client System™
          </h2>
          <p className="text-lg text-brand-gray mb-10 leading-relaxed">
            A landing page designed to
          </p>
        </motion.div>

        <div className="space-y-3 mb-10 max-w-sm mx-auto">
          {features.map((text, i) => (
            <motion.div
              key={i}
              className="flex items-center gap-4 text-left"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
            >
              <Check className="w-5 h-5 text-brand-gold-dark flex-shrink-0" />
              <span className="text-lg text-brand-black font-medium">{text}</span>
            </motion.div>
          ))}
        </div>

        <motion.p className="text-lg text-brand-gray leading-[1.7]" {...fadeUp}>
          Not a template.
          <span className="text-brand-black font-semibold"> Built specifically for your offer.</span>
        </motion.p>
      </div>
    </section>
  );
}

/* ─── 5. WHAT YOU GET ─── */

function WhatYouGetSection() {
  const items = [
    'Explain your offer in seconds',
    'Remove hesitation and confusion',
    'Guide visitors toward a decision',
    'Ready to launch immediately',
  ];

  return (
    <section className="py-24 sm:py-32 px-5 section-warm">
      <div className="max-w-content mx-auto text-center">
        <motion.h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-extrabold text-brand-black mb-14 tracking-tight" {...fadeUp}>
          What You Get
        </motion.h2>

        <div className="space-y-4 mb-8">
          {items.map((text, i) => (
            <motion.div
              key={i}
              className="flex items-center gap-5 text-left bg-white rounded-2xl px-8 py-7 shadow-card border border-brand-gray-light/60 hover:shadow-card-lg transition-shadow duration-300"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
            >
              <span className="w-9 h-9 rounded-full bg-brand-gold/12 flex items-center justify-center flex-shrink-0">
                <Check className="w-4 h-4 text-brand-gold-dark" />
              </span>
              <span className="text-lg text-brand-black font-medium leading-snug">{text}</span>
            </motion.div>
          ))}
        </div>

        <motion.p className="text-base text-brand-gold-dark font-semibold tracking-wide" {...fadeUp}>
          Built for your specific offer. Not generic.
        </motion.p>
      </div>
    </section>
  );
}

/* ─── 6. SCOPE CLARITY ─── */

function ScopeClaritySection() {
  const includes = [
    '1 landing page built to convert',
    'Clear offer positioning and structure',
    'Decision flow from visitor to action',
    'Mobile optimized layout',
  ];

  const excludes = [
    'No ads',
    'No traffic generation',
    'No full branding',
  ];

  return (
    <section className="py-20 sm:py-28 px-5 section-cream">
      <div className="max-w-wide mx-auto">
        <motion.h2 className="text-3xl sm:text-4xl font-extrabold text-brand-black mb-12 tracking-tight text-center" {...fadeUp}>
          What You Actually Get
        </motion.h2>

        <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
          <motion.div
            className="bg-white border border-brand-gray-light/60 rounded-3xl p-9 sm:p-12 shadow-card"
            {...fadeUp}
          >
            <h3 className="text-xl font-extrabold text-brand-black mb-8 flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-brand-gold/12 flex items-center justify-center">
                <Package className="w-5 h-5 text-brand-gold-dark" />
              </span>
              Included
            </h3>
            <div className="space-y-4">
              {includes.map((text) => (
                <div key={text} className="flex items-center gap-3 text-brand-black text-lg font-medium">
                  <Check className="w-4 h-4 text-brand-gold-dark flex-shrink-0" />
                  {text}
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            className="bg-white border border-brand-gray-light/60 rounded-3xl p-9 sm:p-12 shadow-card"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <h3 className="text-xl font-extrabold text-brand-black mb-8 flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-400" />
              </span>
              Not Included
            </h3>
            <div className="space-y-4">
              {excludes.map((text) => (
                <div key={text} className="flex items-center gap-3 text-brand-gray text-lg font-medium">
                  <X className="w-4 h-4 text-red-300 flex-shrink-0" />
                  {text}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─── 7. PROCESS ─── */

function ProcessSection() {
  const days = [
    { label: 'Day 1', title: 'Breakdown', desc: 'We analyze your offer and structure your message.', icon: Target },
    { label: 'Day 2', title: 'Build', desc: 'We design and build your complete landing page.', icon: Sparkles },
    { label: 'Day 3', title: 'Finalize', desc: 'We refine everything and prepare it for launch.', icon: Zap },
  ];

  return (
    <section className="py-24 sm:py-32 px-5 section-warm">
      <div className="max-w-wide mx-auto text-center">
        <motion.h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-extrabold text-brand-black mb-16 tracking-tight" {...fadeUp}>
          How It Works
        </motion.h2>

        <div className="grid sm:grid-cols-3 gap-5">
          {days.map((day, i) => (
            <motion.div
              key={i}
              className="relative bg-white border border-brand-gray-light/60 rounded-3xl p-10 shadow-card hover:shadow-card-lg transition-shadow duration-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.12 }}
            >
              <div className="w-16 h-16 rounded-2xl bg-brand-gold/10 flex items-center justify-center mx-auto mb-6">
                <day.icon className="w-7 h-7 text-brand-gold-dark" />
              </div>
              <div className="text-sm font-bold text-brand-gold-dark uppercase tracking-widest mb-2">
                {day.label}
              </div>
              <div className="text-2xl font-bold text-brand-black mb-3">{day.title}</div>
              <p className="text-sm text-brand-gray leading-relaxed">{day.desc}</p>
              {i < days.length - 1 && (
                <div className="hidden sm:block absolute top-1/2 -right-3.5 transform -translate-y-1/2 text-brand-gray-light">
                  <ArrowRight className="w-5 h-5" />
                </div>
              )}
            </motion.div>
          ))}
        </div>

        <motion.p className="text-base text-brand-black font-semibold mt-10 tracking-wide" {...fadeUp}>
          Your page, ready to use
        </motion.p>
      </div>
    </section>
  );
}

/* ─── 8. EXAMPLE STRUCTURES ─── */

function ExampleSection() {
  const examples = [
    {
      image: proof1,
      category: 'Ecommerce',
      description: 'Guides product discovery and drives purchase decisions.',
    },
    {
      image: proof2,
      category: 'Conversion Structure',
      description: 'Turns attention into action through clarity and flow.',
    },
    {
      image: proof3,
      category: 'Personal Brand',
      description: 'Designed for authority, trust, and strong positioning.',
    },
  ];

  return (
    <section className="py-24 sm:py-32 px-5 section-dark">
      <div className="max-w-wide mx-auto text-center">
        <motion.h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-extrabold text-white mb-4 tracking-tight" {...fadeUp}>
          Example Structures
        </motion.h2>
        <motion.p className="text-lg text-white/50 mb-16 max-w-lg mx-auto leading-relaxed" {...fadeUp}>
          For demonstration only. The same framework adapted across different industries.
        </motion.p>

        <div className="grid sm:grid-cols-3 gap-5">
          {examples.map((card, i) => (
            <motion.div
              key={i}
              className="relative rounded-2xl overflow-hidden bg-white/[0.05] border border-white/10 backdrop-blur-sm hover:border-brand-gold/30 transition-all duration-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.12 }}
            >
              <div className="px-5 pt-5 pb-3">
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-brand-gold bg-brand-gold/10 border border-brand-gold/25 rounded-full px-4 py-1.5">
                  {card.category}
                </span>
              </div>

              <div className="relative mx-4 rounded-xl overflow-hidden shadow-card-dark border border-white/5">
                <img
                  src={card.image}
                  alt={`${card.category} example structure`}
                  className="w-full h-auto"
                />
              </div>

              <div className="px-5 pt-5 pb-7">
                <p className="text-sm text-white/60 leading-relaxed">{card.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── 9. TESTIMONIALS ─── */

function TestimonialSection() {
  const testimonials = [
    {
      name: 'Kapi Levente',
      role: 'Sound and Consciousness Brand',
      quote: 'Before this, people did not really understand what I offer. I had to explain everything manually.\n\nNow the page does it for me. People land on it and already get it.',
    },
    {
      name: null,
      role: 'Ecommerce Founder',
      quote: 'The old page looked clean but it did not sell.\n\nAfter the rebuild, it finally makes sense. People do not just browse anymore. They actually buy.',
    },
    {
      name: null,
      role: 'Personal Brand Coach',
      quote: 'I thought my offer was clear, but it was not.\n\nNow it is simple. People understand it quickly and reach out without confusion.',
    },
    {
      name: null,
      role: 'Service Business Owner',
      quote: 'I had traffic coming in but no consistent clients.\n\nThe new page fixed that. Now I actually get inquiries.',
    },
    {
      name: null,
      role: 'Digital Product Seller',
      quote: 'It is not just design. It is how everything is structured.\n\nYou can feel it is built to guide people to take action.',
    },
    {
      name: null,
      role: 'Small Agency Owner',
      quote: 'I used to send people to a general website. It did not convert.\n\nNow I have one focused page, one clear message. It works.',
    },
  ];

  return (
    <section className="py-24 sm:py-32 px-5 section-cream">
      <div className="max-w-wide mx-auto">
        <motion.div className="text-center mb-16" {...fadeUp}>
          <div className="w-14 h-14 rounded-2xl bg-brand-gold/10 flex items-center justify-center mx-auto mb-7">
            <MessageSquare className="w-7 h-7 text-brand-gold-dark" />
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-extrabold text-brand-black tracking-tight">
            Client Feedback
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-5">
          {testimonials.map((t, i) => (
            <motion.div
              key={i}
              className="bg-white border border-brand-gray-light/60 rounded-3xl p-8 sm:p-10 shadow-card hover:shadow-card-lg transition-shadow duration-300 relative"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.5, delay: (i % 2) * 0.1 }}
            >
              <Quote className="w-8 h-8 text-brand-gold/20 mb-5" />

              <p className="text-brand-black text-[1.05rem] leading-[1.75] whitespace-pre-line mb-7">
                {t.quote}
              </p>

              <div className="flex items-center gap-3 pt-5 border-t border-brand-gray-light/50">
                <div className="w-10 h-10 rounded-full bg-brand-gold/12 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-brand-gold-dark">
                    {t.name ? t.name.charAt(0) : t.role.charAt(0)}
                  </span>
                </div>
                <div>
                  {t.name && <p className="text-sm font-bold text-brand-black">{t.name}</p>}
                  <p className="text-sm text-brand-gray">{t.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.p className="text-center text-lg text-brand-gray mt-14 max-w-lg mx-auto leading-[1.7]" {...fadeUp}>
          Built for people who already have something valuable<br />and need a page that makes others understand it.
        </motion.p>
      </div>
    </section>
  );
}

/* ─── 10. PRICING ─── */

function PricingSection() {
  const included = [
    '1 landing page built to convert',
    'Structured messaging',
    'Mobile optimization',
    'Clear CTA flow',
  ];

  const notIncluded = [
    'Paid ads',
    'Traffic generation',
    'Full branding',
  ];

  return (
    <section className="py-24 sm:py-32 px-5 section-warm">
      <div className="max-w-content mx-auto text-center">
        <motion.h2 className="text-3xl sm:text-4xl font-extrabold text-brand-black mb-14 tracking-tight" {...fadeUp}>
          Simple Pricing
        </motion.h2>

        <motion.div
          className="bg-white rounded-3xl p-10 sm:p-14 shadow-card-lg border border-brand-gray-light/60"
          {...fadeUp}
        >
          <p className="text-sm font-bold text-brand-gold-dark uppercase tracking-widest mb-4">
            3 Day Client System
          </p>
          <h3 className="text-6xl sm:text-7xl font-extrabold text-brand-black mb-5 tracking-tight">
            €349
          </h3>
          <p className="text-lg text-brand-gray leading-[1.7] max-w-md mx-auto mb-10">
            A fast, focused build for founders who need a page that converts. Without agency pricing.
          </p>

          <div className="grid sm:grid-cols-2 gap-8 text-left max-w-md mx-auto mb-10">
            <div>
              <p className="text-xs font-bold text-brand-black uppercase tracking-widest mb-4">What is included</p>
              <div className="space-y-3">
                {included.map((text) => (
                  <div key={text} className="flex items-center gap-2.5 text-sm text-brand-black font-medium">
                    <Check className="w-4 h-4 text-brand-gold-dark flex-shrink-0" />
                    {text}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-bold text-brand-gray uppercase tracking-widest mb-4">What is not included</p>
              <div className="space-y-3">
                {notIncluded.map((text) => (
                  <div key={text} className="flex items-center gap-2.5 text-sm text-brand-gray font-medium">
                    <X className="w-4 h-4 text-red-300 flex-shrink-0" />
                    {text}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <CTAPrimary />
        </motion.div>
      </div>
    </section>
  );
}

/* ─── 11. FINAL CTA ─── */

function FinalCTASection() {
  return (
    <section className="py-28 sm:py-40 px-5 section-dark relative overflow-hidden">
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[600px] h-[400px] bg-brand-gold/[0.06] rounded-full blur-[120px]" />
      </div>

      <div className="max-w-content mx-auto text-center relative z-10">
        <motion.h2
          className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 tracking-tight"
          {...fadeUp}
        >
          Ready to Fix Your Conversion Problem?
        </motion.h2>
        <motion.p className="text-lg text-white/45 mb-14 font-medium" {...fadeUp}>
          Clear process. Fast delivery.
        </motion.p>
        <motion.div {...fadeUp}>
          <CTAGold />
        </motion.div>
      </div>
    </section>
  );
}

/* ─── FOOTER ─── */

function Footer() {
  return (
    <footer className="py-10 px-5 bg-brand-dark border-t border-white/5">
      <div className="max-w-wide mx-auto text-center">
        <p className="text-sm text-white/30 tracking-wide">
          © {new Date().getFullYear()} 3 Day Client System™
        </p>
      </div>
    </footer>
  );
}

/* ─── APP ─── */

function App() {
  return (
    <div className="min-h-screen bg-brand-beige-light">
      <HeroSection />
      <ProblemSection />
      <SolutionSection />
      <SystemSection />
      <WhatYouGetSection />
      <ScopeClaritySection />
      <ProcessSection />
      <ExampleSection />
      <TestimonialSection />
      <PricingSection />
      <FinalCTASection />
      <Footer />
    </div>
  );
}

export default App;
